import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { Box, Typography, Grid, Divider } from '@mui/material';
import { AddCircleOutline as AddIcon } from '@mui/icons-material';

import {
  FBoxContainer,
  FCheckbox,
  FHAntdDateTimePicker,
  FHTextarea,
  FHButton,
  FProgressBar,
  FHSelect,
} from '../../../../../base';
import MaladaptiveBehaviorFields from './components/MaladaptiveBehavior';
import { useTreatmentPlanEditable } from '../../../hooks/useTreatmentPlanEditable';
import { assessmentReviewSchema } from '../../utils/validationSchema';
import { ASSESSMENT_REVIEW_INITIAL_STATE } from '../../constants/validationSchema';
import {
  validateSync,
  getAllInvalidFieldNames,
} from '../../../../../../helpers/validation-helpers';
import { disabledFutureDate, debounce } from '../../../../../../helpers';
import { assessmentActions } from '../../../../../../store';
import { FORTA_WHITE, FORTA_BLUE, FORTA_ERROR } from '../../../../../../utils/colors';
import { DIAGNOSIS_REVIEWED } from '../../constants/general';
import { ERROR_STATES, STATE_IDS, PERSIST_DATA_STATES } from '../../constants/states';
import { getUtcOffset } from '../../../../../../utils/common';
import { TreatmentPlanResourceVersion } from '../../../constants/general';
import { MINIMUM_REQUIRED_SENTENCE_LENGTH, Themes } from '../../../../../../constants';
import ErrorIcon from '../../../../../../assets/images/error-alert.svg';

import type { Moment } from 'moment';
import type { ChangeEvent } from 'react';
import type {
  IInitialAssessment,
  IMaladaptiveBehavior,
  SelectValueType,
} from '../../../../../../utils/types';

function AssessmentReview() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const { assessment, persistDataState, loading } = useSelector((state: any) => state.assessments);
  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const { list: maladaptiveBehaviorList, loading: maladaptiveBehaviorLoading } = useSelector(
    (state: any) => state.maladaptiveBehavior,
  );
  const { id: planId } = treatmentPlan;
  const parentTreatmentPlanId = treatmentPlan?.parent_treatment_plan_id;
  const assessmentVersion = treatmentPlan?.resource_versions.assessment;

  const [addMaladaptiveBehaviour, setAddMaladaptiveBehavior] = useState<boolean>(false);
  const [maladaptiveCount, setMaladeptiveCount] = useState<number>(0);
  const [errors, setErrors] = useState<any>({});
  const [isSummaryValid, setIsSummaryValid] = useState<boolean>(false);

  const [initialAssessmentInfo, setInitialAssessmentInfo] = useState<IInitialAssessment>({
    ...ASSESSMENT_REVIEW_INITIAL_STATE,
  });

  const editable = useTreatmentPlanEditable();

  const updateErrorCount = (errors: any) => {
    const errorCount = Object.values(errors).filter((value) => value).length;
    dispatch(
      assessmentActions.updateErrorState({
        data: {
          [ERROR_STATES[STATE_IDS.ASSESSMENT_REVIEW]]: errorCount,
        },
      }),
    );
  };

  const updatePersistedData = (assessmentReviewData: IInitialAssessment) => {
    const isValid = validateSync(assessmentReviewSchema, {
      ...assessmentReviewData,
      parent_treatment_plan_id: parentTreatmentPlanId,
    });
    // Convert Moment objects to strings before dispatching
    const serializedData = isValid
      ? null
      : {
          ...assessmentReviewData,
          date_of_assessment: assessmentReviewData.date_of_assessment?.format('YYYY-MM-DD') || null,
          date_of_reassessment:
            assessmentReviewData.date_of_reassessment?.format('YYYY-MM-DD') || null,
        };

    dispatch(
      assessmentActions.updatePersistDataState({
        data: {
          [PERSIST_DATA_STATES[STATE_IDS.ASSESSMENT_REVIEW]]: serializedData,
        },
      }),
    );
  };

  const debouncedErrorUpdater = useCallback(debounce(updateErrorCount, 500), []);
  const debouncedDataPersister = useCallback(debounce(updatePersistedData, 500), []);

  useEffect(() => {
    const persistedData = persistDataState[PERSIST_DATA_STATES[STATE_IDS.ASSESSMENT_REVIEW]];
    if (!persistedData) {
      if (!assessment?.id) {
        const errors = getAllInvalidFieldNames(assessmentReviewSchema, {
          ...initialAssessmentInfo,
          parent_treatment_plan_id: parentTreatmentPlanId,
        });
        setErrors(errors);
      }
      return;
    }

    setInitialAssessmentInfo({
      ...persistedData,
      date_of_assessment: persistedData.date_of_assessment
        ? moment(persistedData.date_of_assessment)
        : null,
      date_of_reassessment: persistedData.date_of_reassessment
        ? moment(persistedData.date_of_reassessment)
        : null,
      is_diagnostic_reports_reviewed: getDiagnosisValue(
        persistedData.is_diagnostic_reports_reviewed,
      ),
    });
    const data = {
      date_of_assessment: persistedData.date_of_assessment,
      date_of_reassessment: persistedData.date_of_reassessment,
      clinical_interview_or_summary: persistedData.clinical_interview_or_summary,
      is_diagnostic_reports_reviewed: persistedData.is_diagnostic_reports_reviewed,
    };
    const errorsData = getAllInvalidFieldNames(assessmentReviewSchema, {
      ...data,
      parent_treatment_plan_id: parentTreatmentPlanId,
    });
    setErrors(errorsData);
    // eslint-disable
  }, [planId, persistDataState]);

  useEffect(() => {
    const persistedData = persistDataState[PERSIST_DATA_STATES[STATE_IDS.ASSESSMENT_REVIEW]];
    if (persistedData) return;
    if (!assessment?.id) {
      setInitialAssessmentInfo({ ...ASSESSMENT_REVIEW_INITIAL_STATE });
      const errors = getAllInvalidFieldNames(assessmentReviewSchema, {
        ...ASSESSMENT_REVIEW_INITIAL_STATE,
        parent_treatment_plan_id: parentTreatmentPlanId,
      });
      setErrors(errors);
      return;
    }

    const data = {
      ...initialAssessmentInfo,
      ...assessment,
      date_of_assessment: assessment.date_of_assessment
        ? moment(assessment.date_of_assessment)
        : null,
      date_of_reassessment: assessment.date_of_reassessment
        ? moment(assessment.date_of_reassessment)
        : null,
      is_diagnostic_reports_reviewed: getDiagnosisValue(assessment.is_diagnostic_reports_reviewed),
    };

    setInitialAssessmentInfo(data);
    const dataForValidation = { ...data };
    delete dataForValidation.id;
    delete dataForValidation.summary_observations_during_assessment;
    delete dataForValidation.summary_assessment_result;
    delete dataForValidation.summary_priorities_based_on_assessment;
    const errorObject = getAllInvalidFieldNames(assessmentReviewSchema, {
      ...dataForValidation,
      parent_treatment_plan_id: parentTreatmentPlanId,
    });
    setErrors(errorObject);

    handleValidate(data);

    // eslint-disable
  }, [assessment]);

  useEffect(() => {
    if (maladaptiveBehaviorList.length > maladaptiveCount && addMaladaptiveBehaviour) {
      setAddMaladaptiveBehavior(false);
    }
    if (persistDataState?.[`${PERSIST_DATA_STATES[STATE_IDS.MALADAPTIVE]}-new`]) {
      setAddMaladaptiveBehavior(true);
    } else {
      setAddMaladaptiveBehavior(false);
      dispatch(
        assessmentActions.updateErrorState({
          data: {
            [`${ERROR_STATES[STATE_IDS.MALADAPTIVE]}-new`]: 0,
          },
        }),
      );
    }
    setMaladeptiveCount(maladaptiveBehaviorList.length);
    // eslint-disable
  }, [maladaptiveBehaviorList]);

  useEffect(() => {
    debouncedErrorUpdater(errors);
    // eslint-disable
  }, [errors]);

  useEffect(() => {
    if (!editable) return;
    if (initialAssessmentInfo.date_of_reassessment !== undefined) {
      handleCreateOrUpdateAssessment();
    }
  }, [initialAssessmentInfo.date_of_reassessment]);

  const getDiagnosisValue = (value: boolean | string) => {
    if (typeof value === 'string') {
      return value;
    }
    return value === true ? DIAGNOSIS_REVIEWED.WAS : DIAGNOSIS_REVIEWED.WAS_NOT;
  };

  const handleFieldUpdate = (field: string, value: string | boolean | Moment | null) => {
    const updatedData = { ...initialAssessmentInfo, [field]: value };
    setInitialAssessmentInfo(updatedData);
    debouncedDataPersister(updatedData);
    handleValidate(updatedData);
  };

  const handleValidate = (data: IInitialAssessment = initialAssessmentInfo) => {
    if (!data) return;

    const invalidFields: { [key: string]: boolean } = getAllInvalidFieldNames(
      assessmentReviewSchema,
      {
        parent_treatment_plan_id: parentTreatmentPlanId,
        clinical_interview_or_summary: data.clinical_interview_or_summary,
        date_of_assessment: data.date_of_assessment,
        date_of_reassessment: data.date_of_reassessment,
        is_diagnostic_reports_reviewed: data.is_diagnostic_reports_reviewed,
      },
    );

    const isInValid = Object.keys(invalidFields).some((key) => {
      return invalidFields[key];
    });
    setIsSummaryValid(!isInValid);
    setErrors(invalidFields);
  };

  const handleCreateOrUpdateAssessment = (updates: any = {}) => {
    const data = {
      ...initialAssessmentInfo,
      ...updates,
      date_of_assessment: initialAssessmentInfo.date_of_assessment
        ? initialAssessmentInfo.date_of_assessment.format('YYYY-MM-DD')
        : null,
      date_of_reassessment: initialAssessmentInfo.date_of_reassessment
        ? initialAssessmentInfo.date_of_reassessment.format('YYYY-MM-DD')
        : null,
      date_of_clinical_interview: moment().subtract(1, 'day'),
      date_of_clinical_interview_utc_offset: getUtcOffset(moment()),
    };
    const isValid = validateSync(assessmentReviewSchema, {
      ...data,
      parent_treatment_plan_id: parentTreatmentPlanId,
    });

    if (!isValid || !planId) return;

    data.is_diagnostic_reports_reviewed =
      initialAssessmentInfo.is_diagnostic_reports_reviewed === DIAGNOSIS_REVIEWED.WAS
        ? true
        : false;

    if (!assessment?.id) {
      // create assessment
      dispatch(assessmentActions.createInitialAssessment({ data, getAccessTokenSilently, planId }));
    } else {
      // update assessment
      dispatch(
        assessmentActions.updateInitialAssessment({
          data,
          getAccessTokenSilently,
          planId,
          assessmentId: assessment.id,
        }),
      );
    }
  };

  const handleRemoveNewMaladaptivebehavior = () => {
    setAddMaladaptiveBehavior(false);
    dispatch(
      assessmentActions.updateErrorState({
        data: {
          [`${ERROR_STATES[STATE_IDS.MALADAPTIVE]}-new`]: 0,
        },
      }),
    );
    dispatch(
      assessmentActions.updatePersistDataState({
        data: {
          [`${PERSIST_DATA_STATES[STATE_IDS.MALADAPTIVE]}-new`]: null,
        },
      }),
    );
  };

  return (
    <FBoxContainer colorTheme={Themes.Primary} title="Assessment" sx={{ mt: 3 }}>
      {editable && (
        <Typography
          textAlign={'left'}
          alignSelf="start"
          fontWeight={'700'}
          lineHeight={'21px'}
          marginLeft={'0px'}
          mb={3}
          sx={{
            color: isSummaryValid ? FORTA_BLUE.light1 : FORTA_ERROR,
          }}
        >
          {!isSummaryValid && (
            <img
              style={{ width: '20px', height: '20px', marginRight: '8px', marginTop: '-4px' }}
              src={ErrorIcon}
              alt="attention-icon"
            />
          )}
          Attention: All fields with * are required to be filled
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          flexDirection: 'column',
        }}
      >
        <Grid container>
          <Grid item container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography fontWeight="bold" mb={1} sx={{ ml: 1 }} textAlign="left">
                Initial assessment date*
              </Typography>
              <FHAntdDateTimePicker
                data-testid="initialAssesmentDatePicker"
                format="MM/DD/YY"
                allowClear={false}
                value={initialAssessmentInfo.date_of_assessment}
                disabledDate={disabledFutureDate}
                style={{ width: '100%' }}
                colorTheme={Themes.Primary}
                inputHeight={40}
                disabled={!editable || loading}
                onChange={(e: Moment) => handleFieldUpdate('date_of_assessment', e)}
                onBlur={() => handleCreateOrUpdateAssessment()}
                error={errors['date_of_assessment']}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography fontWeight="bold" textAlign="left" mb={1}>
                Was diagnostic report reviewed*
              </Typography>
              <FHSelect
                data-testid="isDiagnosticReportsReviewedDropDown"
                sx={{ height: 40, width: '100%' }}
                inputHeight={40}
                colorTheme={Themes.Primary}
                disabled={!editable || loading}
                options={[
                  { value: DIAGNOSIS_REVIEWED.WAS, label: 'Reviewed' },
                  { value: DIAGNOSIS_REVIEWED.WAS_NOT, label: 'Not Reviewed' },
                ]}
                value={initialAssessmentInfo.is_diagnostic_reports_reviewed}
                onUpdate={(value: SelectValueType) =>
                  handleFieldUpdate('is_diagnostic_reports_reviewed', value as string)
                }
                onBlur={() => handleCreateOrUpdateAssessment()}
                error={errors['is_diagnostic_reports_reviewed']}
              />
            </Grid>
            {assessmentVersion === TreatmentPlanResourceVersion.V2 && (
              <Grid item xs={12} md={4}>
                <Typography fontWeight="bold" mb={1} sx={{ ml: 1 }} textAlign="left">
                  Re-assessment date{parentTreatmentPlanId ? '*' : ''}
                </Typography>
                <FHAntdDateTimePicker
                  data-testid="reAssessmentDatePicker"
                  format="MM/DD/YY"
                  allowClear={true}
                  value={initialAssessmentInfo.date_of_reassessment}
                  disabledDate={disabledFutureDate}
                  style={{ width: '100%' }}
                  colorTheme={Themes.Primary}
                  inputHeight={40}
                  disabled={!editable || loading}
                  onChange={(e: Moment | null) => {
                    handleFieldUpdate('date_of_reassessment', e || null);
                  }}
                  error={errors['date_of_reassessment']}
                />
              </Grid>
            )}
          </Grid>
          <Grid item md={12} xs={12} mt={2}>
            <Typography fontWeight="bold" textAlign="left" mb={1}>
              Parent interview summary*
            </Typography>
            <FHTextarea
              data-testid="parentInterviewSummaryTextField"
              fullWidth
              minRows={4}
              maxRows={6}
              colorTheme={Themes.Primary}
              disabled={!editable || loading}
              expandable={editable && !loading}
              minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
              value={initialAssessmentInfo.clinical_interview_or_summary}
              onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
                handleFieldUpdate('clinical_interview_or_summary', e.target.value)
              }
              onCloseExpandDialog={() => handleCreateOrUpdateAssessment()}
              onBlur={() => handleCreateOrUpdateAssessment()}
              error={errors['clinical_interview_or_summary']}
              helperText={
                errors['clinical_interview_or_summary']
                  ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                  : ''
              }
            />
          </Grid>
        </Grid>

        <Divider
          sx={{ height: 2, backgroundColor: FORTA_BLUE.primary, my: 3, borderRadius: '1px' }}
        />
        <Box sx={{ display: 'flex', flexGrow: 7 }} mt={0}>
          <Typography fontWeight="bold" mr={2}>
            Additional files viewed:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <FCheckbox
              data-testid="isPreviousAssesmentReportsReviewedCheckbox"
              label="Previous assessment reports"
              sx={{ mb: 2 }}
              colorTheme={Themes.Primary}
              disabled={!editable || loading}
              checked={initialAssessmentInfo.is_previous_assessment_reports_reviewed}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                handleFieldUpdate('is_previous_assessment_reports_reviewed', e.target.checked);
                handleCreateOrUpdateAssessment({
                  is_previous_assessment_reports_reviewed: e.target.checked,
                });
              }}
            />
            <FCheckbox
              data-testid="isPreviousInterventionTreatmentPlansReviewedCheckbox"
              label="Previous intervention plans and treatment plans"
              sx={{ mb: 2 }}
              colorTheme={Themes.Primary}
              disabled={!editable || loading}
              checked={
                initialAssessmentInfo.is_previous_intervention_plans_and_treatment_plans_reviewed
              }
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                handleFieldUpdate(
                  'is_previous_intervention_plans_and_treatment_plans_reviewed',
                  e.target.checked,
                );
                handleCreateOrUpdateAssessment({
                  is_previous_intervention_plans_and_treatment_plans_reviewed: e.target.checked,
                });
              }}
            />
            <FCheckbox
              data-testid="isReportsByOtherProvidersReviewedCheckbox"
              label="Reports by other providers"
              sx={{ mb: 1.5 }}
              colorTheme={Themes.Primary}
              disabled={!editable || loading}
              checked={initialAssessmentInfo.is_reports_by_other_providers_reviewed}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                handleFieldUpdate('is_reports_by_other_providers_reviewed', e.target.checked);
                handleCreateOrUpdateAssessment({
                  is_reports_by_other_providers_reviewed: e.target.checked,
                });
              }}
            />
            <FCheckbox
              data-testid="isSchoolReportsReviewedCheckbox"
              label="School reports"
              sx={{ mb: 1.5 }}
              colorTheme={Themes.Primary}
              disabled={!editable || loading}
              checked={initialAssessmentInfo.is_school_reports_reviewed}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                handleFieldUpdate('is_school_reports_reviewed', e.target.checked);
                handleCreateOrUpdateAssessment({
                  is_school_reports_reviewed: e.target.checked,
                });
              }}
            />
            <FCheckbox
              data-testid="isMedicalReportsReviewedCheckbox"
              label="Medical reports"
              sx={{ mb: 1.5 }}
              colorTheme={Themes.Primary}
              disabled={!editable || loading}
              checked={initialAssessmentInfo.is_medical_reports_reviewed}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                handleFieldUpdate('is_medical_reports_reviewed', e.target.checked);
                handleCreateOrUpdateAssessment({
                  is_medical_reports_reviewed: e.target.checked,
                });
              }}
            />
          </Box>
        </Box>
        <Divider
          sx={{ height: 2, backgroundColor: FORTA_BLUE.primary, my: 3, borderRadius: '1px' }}
        />
        <Box>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            {'Maladaptive behavior (if present or reported by parents):'}
          </Typography>
          <Box>
            {maladaptiveBehaviorList.map((mb: IMaladaptiveBehavior, index: number) => (
              <MaladaptiveBehaviorFields maladaptiveBehavior={mb} key={index} />
            ))}
            {addMaladaptiveBehaviour && (
              <MaladaptiveBehaviorFields
                removeNewMaladaptivebehavior={handleRemoveNewMaladaptivebehavior}
              />
            )}
          </Box>
          {editable && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <FHButton
                data-testid="addMaladaptiveBehaviour"
                colorTheme={Themes.Primary}
                disabled={addMaladaptiveBehaviour || maladaptiveBehaviorLoading}
                startIcon={<AddIcon sx={{ color: FORTA_WHITE }} />}
                onClick={() => setAddMaladaptiveBehavior(true)}
              >
                Add Maladaptive Behavior
              </FHButton>
            </Box>
          )}
          {maladaptiveBehaviorLoading && <FProgressBar />}
        </Box>
      </Box>
    </FBoxContainer>
  );
}

export default AssessmentReview;
